// assets/product-custom-property.js
import { Component } from '@theme/component';

/**
 * @typedef {object} ProductCustomPropertyRefs
 * @property {HTMLInputElement | HTMLTextAreaElement} textInput - The text input.
 * @property {HTMLElement} characterCount - The character count element.
 */

/**
 * A custom element that manages product custom properties.
 *
 * Optionally shows/hides itself based on the currently selected variant option.
 * When the field wrapper carries `data-cpp-conditional="<needle>"`, the field is
 * only shown while a selected variant option value contains `<needle>` (case
 * insensitive). While hidden, the input is disabled so its value is never
 * submitted as a line-item property.
 *
 * @extends Component<ProductCustomPropertyRefs>
 */
class ProductCustomProperty extends Component {
  /** @type {HTMLElement | null} */
  #wrapper = null;

  /** @type {string} */
  #optionNeedle = '';

  #handleVariantChange = () => this.#applyConditionalVisibility();

  connectedCallback() {
    super.connectedCallback();

    this.#wrapper = this.querySelector('[data-cpp-conditional]');
    this.#optionNeedle = (this.#wrapper?.dataset.cppConditional ?? '').trim().toLowerCase();

    // No condition configured — field is always visible, nothing to wire up.
    if (!this.#optionNeedle) return;

    document.addEventListener('variant:update', this.#handleVariantChange);
    document.addEventListener('change', this.#handleVariantChange);
    this.#applyConditionalVisibility();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (!this.#optionNeedle) return;

    document.removeEventListener('variant:update', this.#handleVariantChange);
    document.removeEventListener('change', this.#handleVariantChange);
  }

  handleInput() {
    this.#updateCharacterCount();
  }

  #applyConditionalVisibility() {
    if (!this.#optionNeedle || !this.#wrapper) return;

    const shouldShow = this.#isNeedleSelected();
    this.#wrapper.toggleAttribute('hidden', !shouldShow);

    const { textInput } = this.refs;
    if (textInput) textInput.disabled = !shouldShow;
  }

  /**
   * @returns {boolean} Whether any selected variant option value matches the needle.
   */
  #isNeedleSelected() {
    const section = this.closest('.shopify-section') ?? document;
    const pickers = section.querySelectorAll('variant-picker');
    const scopes = pickers.length ? pickers : [section];

    for (const scope of scopes) {
      // Button / radio style variant pickers.
      for (const radio of scope.querySelectorAll('input[type="radio"]:checked')) {
        if (this.#matches(/** @type {HTMLInputElement} */ (radio).value)) return true;
      }
      // Dropdown style variant pickers.
      for (const select of scope.querySelectorAll('select')) {
        const sel = /** @type {HTMLSelectElement} */ (select);
        if (this.#matches(sel.options[sel.selectedIndex]?.value)) return true;
      }
    }

    return false;
  }

  /**
   * @param {string | undefined} value
   * @returns {boolean}
   */
  #matches(value) {
    return (value ?? '').toLowerCase().includes(this.#optionNeedle);
  }

  #updateCharacterCount() {
    const { characterCount, textInput } = this.refs;
    const currentLength = textInput.value.length;
    const maxLength = textInput.maxLength;

    const template = characterCount.getAttribute('data-template');
    if (!template) return;

    const updatedText = template.replace('[current]', currentLength.toString()).replace('[max]', maxLength.toString());

    characterCount.textContent = updatedText;
  }
}

customElements.define('product-custom-property-component', ProductCustomProperty);
